// File: src/pages/RoutingLearningPath.js
// "Learning to Route": the concepts course behind the fleet-routing report.
// Each section carries one message, a figure drawn for that message, prose
// that replaces the live narration, a lab where a whiteboard used to be, and
// a panel with what the September 2026 research added.

import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { anchors, sections, evidence, glossary, timeline, readingList } from "../data/routingLearningPath";
import { LocalSearchFlow, LocalSearchLab, SupervisedLab, QLab } from "./routingLabs";
import {
  TspFigure,
  SupervisedLoopFigure,
  ThreeUsesFigure,
  GeneralizationFigure,
  DecomposeFigure,
  RlLoopFigure,
  PolicyFigure,
  ActionTreeFigure,
  RevisableFigure,
  InferenceCostFigure,
  EncoderFigure,
  PipelineFigure,
} from "./routingFigures";
import "./RoutingLearningPath.css";

const PROGRESS_KEY = "rlp-progress-v1";

const GROUPS = [
  { name: "The problem, and how it is solved today", ids: ["problem", "local-search"] },
  { name: "Learning by copying", ids: ["supervised", "sl-limits"] },
  { name: "Learning by expecting", ids: ["rl", "explore", "rl-limits", "rl-inside"] },
  { name: "Where it sits, where it went", ids: ["gnn", "2026"] },
];

/* ----------------------------------------------------------------------- */
/* Shared pieces                                                            */
/* ----------------------------------------------------------------------- */

function Evidence({ id }) {
  const block = evidence[id];
  if (!block) return null;
  return (
    <details className="rlp-evidence">
      <summary>
        What the 2026 evidence adds
        <span className="rlp-evidence-year">research report, Sep 2026</span>
      </summary>
      <div className="rlp-evidence-body">
        <p>{block.intro}</p>
        <ul>
          {block.items.map((it, i) => (
            <li key={i}>
              {it.text}
              {it.link && anchors[it.link] && (
                <>
                  {" "}
                  <Link to={anchors[it.link]}>in the report</Link>
                </>
              )}
            </li>
          ))}
        </ul>
        {block.more && <p className="rlp-more">{block.more}</p>}
      </div>
    </details>
  );
}

function SectionShell({ meta, done, onToggle, lead, children }) {
  return (
    <section className="rlp-section" id={meta.id}>
      <div className="rlp-section-head">
        <span className="rlp-section-num">{meta.num}</span>
        <h2>{meta.title}</h2>
        <label className="rlp-done-toggle">
          <input type="checkbox" checked={!!done} onChange={onToggle} />
          mark as understood
        </label>
      </div>
      {lead && <p className="rlp-lead">{lead}</p>}
      {children}
    </section>
  );
}

function Aside({ title, children }) {
  return (
    <div className="rlp-subtext">
      {title && <strong>{title}</strong>} {children}
    </div>
  );
}

function CourseMap({ done, jump }) {
  const meta = Object.fromEntries(sections.map((s) => [s.id, s]));
  return (
    <nav className="rlp-map" aria-label="course map">
      {GROUPS.map((g) => (
        <div key={g.name} className="rlp-map-group">
          <div className="rlp-map-group-name">{g.name}</div>
          <ol className="rlp-map-steps">
            {g.ids.map((id) => (
              <li key={id}>
                <button type="button" className={`rlp-step${done[id] ? " done" : ""}`} onClick={() => jump(id)}>
                  <span className="rlp-step-num">{done[id] ? "✓" : meta[id].num}</span>
                  <span className="rlp-step-title">{meta[id].title}</span>
                </button>
              </li>
            ))}
          </ol>
        </div>
      ))}
      <p className="rlp-map-note">Click a step to jump. Ticks appear when you mark a section as understood; progress is stored in this browser only.</p>
    </nav>
  );
}

function loadProgress() {
  try {
    const raw = window.localStorage.getItem(PROGRESS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
}

/* ----------------------------------------------------------------------- */
/* Page                                                                     */
/* ----------------------------------------------------------------------- */

export default function RoutingLearningPath() {
  const [done, setDone] = useState(loadProgress);

  useEffect(() => {
    try {
      window.localStorage.setItem(PROGRESS_KEY, JSON.stringify(done));
    } catch (e) {
      /* storage unavailable */
    }
  }, [done]);

  const toggle = (id) => setDone((d) => ({ ...d, [id]: !d[id] }));
  const jump = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const count = sections.filter((s) => done[s.id]).length;
  const meta = Object.fromEntries(sections.map((s) => [s.id, s]));

  return (
    <div className="rlp-page">
      <Helmet>
        <title>Learning to Route | Helia Jamshidi</title>
        <meta
          name="description"
          content="An interactive course on machine learning for vehicle routing: local search, supervised and reinforcement learning on the travelling salesman problem, where each breaks, and what the 2026 evidence says. Companion to Fleet Routing Without Memory."
        />
        <meta name="keywords" content="reinforcement learning, combinatorial optimization, TSP, VRP, local search, HGS, PyVRP, neural combinatorial optimization" />
        <meta name="author" content="Helia Jamshidi" />
        <meta property="og:title" content="Learning to Route | Helia Jamshidi" />
        <meta
          property="og:description"
          content="Local search, supervised and reinforcement learning on the travelling salesman problem, with labs, and what the 2026 evidence says about each."
        />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://heliajamshidi.me/writing/technology/learning-to-route" />
        <link rel="canonical" href="https://heliajamshidi.me/writing/technology/learning-to-route" />
      </Helmet>

      <header className="rlp-masthead">
        <p className="rlp-eyebrow">Course notes with labs · September 2026</p>
        <h1>Learning to route</h1>
        <p className="rlp-lede">
          This is the ground floor under <Link to={anchors.report}>Fleet Routing Without Memory</Link>. That report assumes you know what a
          metaheuristic is, why a supervised solver cannot beat its teacher, and what a reinforcement learner actually estimates. This page
          teaches those ideas on the smallest problem that shows them, with a lab where a whiteboard used to be. Each section ends with what
          the 2026 evidence added, so the concepts land where the field actually went.
        </p>
        <div className="rlp-companion">
          <strong>Read alongside:</strong>
          <Link to={anchors.onePage}>the field in one page</Link>
          <Link to={anchors.thread1}>learned solvers versus metaheuristics</Link>
          <Link to={anchors.builder}>a builder's minimum viable system</Link>
        </div>
      </header>

      <div className="rlp-progress-row">
        <span>
          {count} of {sections.length} sections understood
        </span>
        <span className="rlp-progress-bar">
          <i style={{ width: `${(count / sections.length) * 100}%` }} />
        </span>
        {count > 0 && (
          <button type="button" className="rlp-reset" onClick={() => setDone({})}>
            reset
          </button>
        )}
      </div>

      <CourseMap done={done} jump={jump} />

      <div className="rlp-body">
        {/* 01 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta.problem}
          done={done.problem}
          onToggle={() => toggle("problem")}
          lead="Given a set of stops and the travel cost between every pair, find the cheapest closed tour that visits each stop exactly once. Everything in this course is practised on this one problem, because it fits in one picture and every routing method can be shown on it."
        >
          <TspFigure />
          <p>
            The input is two things: the stops, and a number for every pair of stops. The output is an order. The cost of an order is the
            sum of the pair costs along it, back to the start. With N stops there are (N-1)!/2 distinct tours. For nine stops that is
            20,160, which a computer checks in a blink. For the 148 stops of a typical parcel-van route it is a number with more than 250
            digits, which is why nobody checks them all and why the rest of this course exists.
          </p>
          <Aside title="From the tour to the fleet.">
            The fleet version is the vehicle routing problem: several vehicles leaving a depot, each with a capacity, each stop with a time
            window, and travel times instead of distances. The mechanics are the same. The difference is what the number being minimized
            means. A fleet does not sell short tours; it sells punctual deliveries at a cost. Keep that in mind every time this course says
            "cost".
          </Aside>
          <Evidence id="problem" />
        </SectionShell>

        {/* 02 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta["local-search"]}
          done={done["local-search"]}
          onToggle={() => toggle("local-search")}
          lead="Start anywhere. Change something small. Keep it if the tour got cheaper. Usually undo it if not, but sometimes keep it anyway. Stop when you run out of time. This loop is how routing has been solved in practice since the 1990s, and every named metaheuristic is a strategy wrapped around it."
        >
          <LocalSearchFlow />
          <p>
            A small change means reversing a segment of the tour, moving one stop somewhere else, or swapping two stops. Simulated annealing
            decides the "faith" question with a temperature that cools over time. Iterated local search restarts from a perturbed copy of
            the best tour when it is stuck. A genetic algorithm keeps a population of tours and breeds new starting points from pairs of good
            ones. Hybrid genetic search, the reference solver in this field, is exactly that population plus this loop run on every child.
          </p>
          <Aside title="Why faith is not a joke.">
            A tour that no single small change can improve is a local optimum. It may be far from the best tour, but from inside it every
            direction looks uphill. Accepting a worse tour once in a while is the only way out without starting over. Too little faith and
            you get stuck early. Too much and you wander like a random walk. Most of the engineering in a good solver is deciding how much
            faith to have, and when.
          </Aside>
          <LocalSearchLab learned={false} />
          <Aside title="What to notice in the lab.">
            With faith at zero the run stalls at a local optimum within a few dozen iterations; check the gap. With faith around 6 to 10 it
            usually finds the optimum. At 20 it rarely settles. Tick the near-neighbour box and the same budget goes further: that is the
            heat-map idea from the next section, done with plain geometry instead of a network. Watch the time counter: the guided moves
            cost model calls.
          </Aside>
          <Evidence id="local-search" />
        </SectionShell>

        {/* 03 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta.supervised}
          done={done.supervised}
          onToggle={() => toggle("supervised")}
          lead="Supervised learning is copying. Run a slow exact solver on thousands of small problems to get labelled examples, then train a network to reproduce the labels from the inputs. The loss measures how the model's tour differs from the label. It does not know what a good tour is; it knows what the label was."
        >
          <SupervisedLoopFigure />
          <p>
            The training data is pairs: an input (stops and costs) and an expected output (the optimal tour, or a near-optimal one when the
            exact solver is too slow). The model produces a tour; the loss counts the label's edges that the model's tour does not contain;
            gradient descent nudges the parameters so that the count is smaller next time. If you are lucky, the model learns to produce
            outputs that look like the labels.
          </p>
          <Aside title="The sentence to hold on to.">
            Even if the model's tour has less cost than the label, there is a loss while training. A copy can at best equal its teacher. In
            practice it does worse, because the teacher's solution is one of many and the copy is approximate. This explains half of the
            limitations in the next section.
          </Aside>
          <SupervisedLab />
          <h3>Three ways to use a supervised model</h3>
          <p>
            Once a model has been trained to imitate good tours, there are three different things you can do with it, and they lead to very
            different places. Take one small delivery run: five stops, the cost of every pair, and one rule the planner must respect, that
            stop c has to be reached before 10:00.
          </p>
          <ol>
            <li>
              <b>One shot.</b> Ask the model for the whole order and drive it. Fast, and nothing checks the answer. The model has only ever seen
              tours; it has never seen the rule about c, so it can hand you an order that is a little long and, worse, illegal.
            </li>
            <li>
              <b>Guide the search.</b> Do not ask the model for an order at all. Ask it, for every pair of stops, how likely that pair is to
              belong to a good tour. That table of likelihoods is the heat map. Hand it to the search loop from section 02, which now spends
              its small changes on the pairs the map rates high and checks every change against the rule. The model advises; the solver
              decides and verifies.
            </li>
            <li>
              <b>Pre-train, then reinforcement learning.</b> Start with the imitating model, then keep training it on its own tours with a reward
              (minus the tour length) instead of labels, which is section 05. The tours get shorter than the labeller's and no labeller is
              needed any more. But the output is still one order, produced in one shot, so the rule is still only respected if something
              else checks it.
            </li>
          </ol>
          <ThreeUsesFigure />
          <Aside title="The heat map is the idea that lasted.">
            A trained model does not need to output a whole tour. It can output, per edge, how likely that edge is to belong to a good
            tour, and a search can then spend its budget on the edges the map likes. That is a soft version of the near-neighbour lists every
            good TSP solver has used since the 1970s. The lab in section 02 shows what such guidance does to a search budget.
          </Aside>
          <Evidence id="supervised" />
        </SectionShell>

        {/* 04 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta["sl-limits"]}
          done={done["sl-limits"]}
          onToggle={() => toggle("sl-limits")}
          lead="Four limits follow from copying. The labels need an exact solver, which stops being available exactly where a learned method would help most. A model trained at one size degrades at another. It needs retraining when the world changes. And when a problem has several equally good tours, the label is an arbitrary one of them."
        >
          <h3>Data dependency and size</h3>
          <p>
            The optimal solution is hard or impossible to compute for large instances, so the training set is made of small ones. The model
            is then asked to generalize to sizes it has never seen, which is where we need it most. The figure shows what happens.
          </p>
          <GeneralizationFigure />
          <h3>Flexibility</h3>
          <p>
            A model trained on one region, one season, one set of contracts encodes those conditions. New territory, new regulations, a
            different demand pattern: the labels no longer describe the world and the model needs retraining before it is trustworthy. It
            is also the wrong tool for decisions that must react in real time, since retraining is slow.
          </p>
          <h3>One label per instance</h3>
          <p>
            The labelling solver has no reason to prefer one optimal tour over another, and heuristic labellers are stochastic. Two nearly
            identical problems can carry labels that look nothing alike. The network is asked to learn a function where similar inputs map to
            dissimilar outputs, which is the definition of a hard function to learn. The consequences: loss of solution diversity, a
            preference for arbitrary solutions, rigidity when constraints change, and a model that may never converge or overfits. The lab in
            section 03 shows the loss an equally good tour receives; multiply that across a training set and the gradients point in
            contradictory directions.
          </p>
          <h3>Is that a dead end?</h3>
          <p>
            No. A supervised model can still be much faster than a solver, and its output is a good starting point for a method that does
            not learn. And the size problem has a practical escape: train on small, fixed-size problems, and at solve time divide a big
            problem into pieces of that size.
          </p>
          <DecomposeFigure />
          <Evidence id="sl-limits" />
        </SectionShell>

        {/* 05 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta.rl}
          done={done.rl}
          onToggle={() => toggle("rl")}
          lead="Reinforcement learning drops the labels. There is no expected output. There is an environment that answers every action with a reward and a new state, and a model trained not to copy but to expect: for each action it could take, what is the total reward that follows?"
        >
          <RlLoopFigure />
          <p>
            The model can be used three ways: on its own, building the tour one decision at a time by calling the model once per stop;
            with several agents, one per vehicle or per depot; or inside a metaheuristic, which is section 08. The vocabulary is the same
            in all three. The state is what the model can see when it decides. The action is one decision. The reward is the number the
            environment returns. A session runs from the first decision to termination. The value of a state, or of an action in a state,
            is the sum of rewards expected from there to the end.
          </p>
          <Aside title="So where is the expectation, and how does it learn?">
            Think of a weekend. The session is Saturday; the value is your satisfaction at the end of it. The state: you are healthy, in
            the Alps at noon, with two meals packed. The model's estimates for the actions in that state: hike up to the lake 12, lunch from
            the backpack minus 5, lunch in the hut 10, two lunch beers minus 10, jump off the cliff minus 1000. The model is a value
            estimator. Acting well means asking it and taking the best action. Learning means correcting its estimates with what then
            happens. The lab makes the model a table so you can watch that correction.
          </Aside>
          <QLab />
          <Aside title="What to notice in the lab.">
            From state I the table says C is best. Take it: the environment returns reward 7 and state IV, where the best the table promises
            is 3. So (I, C) was worth about 10, not 3, and the estimate moves up. That is the whole learning rule: what the model thinks
            versus what the model observes, and a step from one toward the other. The learning rate is the size of the step; the discount
            says how much the future counts. In reality the table is a deep network and the states are millions of partial tours, but the
            rule is the same.
          </Aside>
          <h3>A short history</h3>
          <ul className="rlp-timeline">
            {timeline.map((t, i) => (
              <li key={i}>
                <span className="yr">{t.yr}</span>
                {t.text}
              </li>
            ))}
          </ul>
          <Evidence id="rl" />
        </SectionShell>

        {/* 06 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta.explore}
          done={done.explore}
          onToggle={() => toggle("explore")}
          lead="A learner that always takes the best-known action never finds out whether a better one exists. A learner that always tries new things never gets good at anything. Every reinforcement method is a position on that trade-off, and there are two ways to own it."
        >
          <PolicyFigure />
          <p>
            <b>Off-policy</b> learning, of which Q-learning is the classic, lets you behave one way and learn about another. You can explore at
            random while the table keeps estimating the value of the greedy policy. Exploring shows you other states and other strategies,
            and gets you out of local optima. Exploiting gets you the best estimate of your current best strategy and takes you to the top
            of the hill you are on. The price of off-policy learning is that estimates for actions you rarely take stay stale.
          </p>
          <p>
            <b>On-policy</b> learning makes the model output a probability per action and follow it. Exploration is built in: early on the
            probabilities are flat and the model tries many things; as they sharpen it settles into a stable strategy. The model owns the
            trade-off. <b>Tree search</b> sits on top of either: before committing, simulate many futures using the model's probabilities as a
            guide, and take the action whose simulated futures looked best. AlphaZero is a policy network, a value network and this tree
            search.
          </p>
          <Aside title="Try it in the lab above.">
            Switch between the three policies and press the simulate button. Early on the tree search often disagrees with the greedy
            pick; after the table has been corrected for a while, it agrees.
          </Aside>
          <Evidence id="explore" />
        </SectionShell>

        {/* 07 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta["rl-limits"]}
          done={done["rl-limits"]}
          onToggle={() => toggle("rl-limits")}
          lead="Used directly to build a route, a reinforcement learner runs into three walls: it has no notion of constraints, the number of actions and states is astronomical, and it accepts a rule that routing never asked for, that a decision once made cannot be revised."
        >
          <h3>Constraints and the size of the action space</h3>
          <ActionTreeFigure />
          <p>
            The model has no notion of a time window or a capacity. Constraints can be imposed by masking actions that would violate them,
            which works for simple constraints and fails for rich ones: follow masked branches far enough and you reach a state with no
            legal action left. Independently of constraints, most actions in most states are irrelevant, and a learner that does not know
            that yet must try them. The set of policies to explore is not tractable, knowledge gained from one action does not transfer to
            similar actions on its own, and a large share of states is never visited. Learning an effective policy typically needs a very
            large number of samples, and even when possible it is expensive.
          </p>
          <h3>Mitigations</h3>
          <ul>
            <li>
              <b>Shrink the action space with knowledge</b>: expert rules, or a supervised heat map that prunes candidates before the learner
              sees them.
            </li>
            <li>
              <b>Hierarchical decisions</b>: group actions into higher-level choices so the effective branching is small.
            </li>
            <li>
              <b>Defer the hard decisions</b>: at each step the agent decides whether to commit now or postpone; the easy decisions go first
              and the hard ones are made once the candidate set has narrowed.
            </li>
            <li>
              <b>Cluster first</b>, then solve sub-problems, the same escape as in section 04.
            </li>
          </ul>
          <h3>The limitation nobody imposed</h3>
          <RevisableFigure />
          <Aside title="This is the most important point for a practitioner.">
            Reinforcement learning grew up on problems where time only moves forward: chess, video games, robots. Planning is not such a
            problem. A plan on a screen can be revised for free until the first van leaves. Building a tour city by city and never
            revisiting a choice throws that freedom away. The natural repair is the mixed design in the next section: keep the search loop,
            which revises freely, and let the learner decide what the loop should try next.
          </Aside>
          <Evidence id="rl-limits" />
        </SectionShell>

        {/* 08 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta["rl-inside"]}
          done={done["rl-inside"]}
          onToggle={() => toggle("rl-inside")}
          lead="Keep the loop from section 02. Replace one coin flip inside it with a learner. The learner can choose which operator to apply, which stops or edges to touch, how to set the solver's parameters, or how to cut the problem into pieces. Tree search over the same loop is another member of the family."
        >
          <LocalSearchFlow guided />
          <p>
            The classic loop picks the type of small change at random: reverse a segment, swap two stops, move one stop, move a pair. In
            the guided version a trained model looks at the current solution and picks the operator. Everything after that is unchanged,
            including faith and tiredness. The same pattern applies one level down: a model can pick which stops to remove for a destroy
            operator and where to reinsert them, or which edges are promising to add.
          </p>
          <h3>How that learner is trained</h3>
          <div className="rlp-tablewrap">
            <table className="rlp-table">
              <tbody>
                <tr>
                  <th>State</th>
                  <td>the current solution</td>
                </tr>
                <tr>
                  <th>Action</th>
                  <td>one of the four operators</td>
                </tr>
                <tr>
                  <th>Session</th>
                  <td>one local-search run, from the initial solution until tired</td>
                </tr>
                <tr>
                  <th>Reward</th>
                  <td>the reduction in the cost of the solution after the change</td>
                </tr>
              </tbody>
            </table>
          </div>
          <LocalSearchLab learned />
          <h3>The cost of asking</h3>
          <InferenceCostFigure />
          <Aside title="What to notice in the lab.">
            Set the model call cost to zero and the learned picker usually reaches the optimum in fewer iterations than the random one. Set
            it to 10 evaluations per call and compare the simulated time at the moment each reaches the optimum. On a real solver a
            local-search iteration is microseconds and a network forward pass is milliseconds, so the learner has to be far more decisive per
            call to break even. That arithmetic, not the quality of the learned policy, decides most published comparisons.
          </Aside>
          <Evidence id="rl-inside" />
        </SectionShell>

        {/* 09 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta.gnn}
          done={done.gnn}
          onToggle={() => toggle("gnn")}
          lead="Every model in this course reads a set of stops. A set has no natural order and no fixed size, so the network that encodes it must not care about either. That is what graph neural networks and transformers are for, and it is why they are the encoders in every paper in this field."
        >
          <EncoderFigure />
          <p>
            A graph network passes messages between neighbouring stops, so each stop's encoding summarizes its surroundings. A transformer
            lets every stop attend to every other, so the encoding can capture long-range structure at a higher cost. The attention model
            that became the reference reinforcement solver is a transformer encoder over the stops followed by a decoder that points at the
            next one. The heat maps of section 03 come from a graph convolutional network over the edges. The model drawn as a table in the
            labs above is, in every paper, one of these encoders.
          </p>
          <Aside title="Multi-agent">
            reframes a fleet as a game: each vehicle, or each depot, is an agent that picks its next stop, with a shared or competing reward.
            It appears in the 2026 evidence mostly as a research library rather than a production result, so this course leaves it as a
            pointer.
          </Aside>
          <Evidence id="gnn" />
        </SectionShell>

        {/* 10 ------------------------------------------------------------ */}
        <SectionShell
          meta={meta["2026"]}
          done={done["2026"]}
          onToggle={() => toggle("2026")}
          lead="The course asks whether a learned model can replace the metaheuristic. The report written in September 2026 answers that question and then changes it: the field spent a decade trying to learn the solver and has just started learning the inputs."
        >
          <div className="rlp-tablewrap">
            <table className="rlp-table">
              <thead>
                <tr>
                  <th>What the course teaches</th>
                  <th>What the 2026 report measured</th>
                  <th>Where to read more</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Local search with faith is how routing is solved.</td>
                  <td>
                    Still true. HGS reaches a 0.29% gap in 111 s on the standard benchmark; nothing learned beats HGS or PyVRP at equal
                    wall-clock on equal hardware under 2,000 stops.
                  </td>
                  <td>
                    <Link to={anchors.thread1}>Thread 1</Link>
                  </td>
                </tr>
                <tr>
                  <td>A supervised copy cannot beat its teacher and does not generalize.</td>
                  <td>Measured: trained at 100, 128.9% off at 1,000; 0.72% on the benchmark becomes 10 to 77% on real instances.</td>
                  <td>
                    <Link to={anchors.thread1}>Thread 1</Link>
                  </td>
                </tr>
                <tr>
                  <td>End-to-end RL construction imposes an unnecessary limitation.</td>
                  <td>
                    The field's own survey: best neural constructive solver 4.10% versus HGS 0.29%; many neural solvers lose to
                    nearest-neighbour construction. The leading builders of that line left the field.
                  </td>
                  <td>
                    <Link to={anchors.thread1}>Thread 1</Link>
                  </td>
                </tr>
                <tr>
                  <td>Learn which small change to make inside the loop.</td>
                  <td>
                    The one durable idea: learn what to destroy (beats HGS at 1,000 to 2,000 stops, with a GPU), where to partition, what to
                    dispatch, and since 2025 which operator code to run.
                  </td>
                  <td>
                    <Link to={anchors.thread2}>Thread 2</Link>
                  </td>
                </tr>
                <tr>
                  <td>Exploration versus exploitation is the learner's central dilemma.</td>
                  <td>
                    In dispatch, anticipating the future is worth 10 to 13 points over greedy; a learned policy beats plain scenario
                    sampling by about 3. Sample first.
                  </td>
                  <td>
                    <Link to={anchors.thread4}>Thread 4</Link>
                  </td>
                </tr>
                <tr>
                  <td>Inference cost decides iterative methods.</td>
                  <td>Confirmed as the norm: learned methods must run at solver time budgets; equal-wall-clock wins need a GPU on the learned side.</td>
                  <td>
                    <Link to={anchors.thread1}>Thread 1</Link>
                  </td>
                </tr>
                <tr>
                  <td>(Not in the course) Learn from the fleet's own history.</td>
                  <td>
                    Tested once in public. The winner trained nothing: zone parsing, one reference route, penalty-based LKH. Drivers
                    deviate from plans 77% of the time and drive 1.5% shorter when they do.
                  </td>
                  <td>
                    <Link to={anchors.thread3}>Thread 3</Link>
                  </td>
                </tr>
                <tr>
                  <td>(Not in the course) The travel-time matrix.</td>
                  <td>
                    Learned ETA halves a lookup's error at platforms; a mid-market fleet cannot buy a time-dependent matrix at a sane
                    price, and a static one makes 4 to 8% of stops late.
                  </td>
                  <td>
                    <Link to={anchors.thread5}>Thread 5</Link>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <PipelineFigure />
          <p>
            This course lives inside the Plan box. The report's conclusion is that the Plan box is saturated and the money is in the other
            three: learned predictions feeding a classic solver, which is what the large delivery platforms actually run; a Learn stage that
            nobody outside those platforms has built; and a matrix that is unpriced rather than unmodelled. For the reader who came here to
            understand why a learned solver is not the first thing to build, that is the answer.
          </p>
          <Evidence id="2026" />
          <h3>If you read seven things after this page</h3>
          <ol>
            {readingList.map(([what, why], i) => (
              <li key={i}>
                <b>{what}.</b> {why}
              </li>
            ))}
          </ol>
          <p>
            The full ranked list, with the history and matrix papers, is in the report: <Link to={anchors.reading}>reading list, ranked</Link>.
          </p>
        </SectionShell>

        <section className="rlp-section" id="glossary">
          <div className="rlp-section-head">
            <span className="rlp-section-num">Glossary</span>
            <h2>The words, in one place</h2>
          </div>
          <dl className="rlp-glossary">
            {glossary.map(([term, def]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>{def}</dd>
              </div>
            ))}
          </dl>
        </section>
      </div>

      <p className="rlp-foot">
        The evidence panels quote the September 2026 research report, and each one links to the section it came from. The figures are drawn
        for the idea they carry, not to scale, except where a caption names the paper the numbers come from. The labs are toy models built
        to show one mechanism each, not benchmarks: nine stops, four operators, a four-by-four table. Where a date in the history is from
        memory rather than from a source I re-read, the text says so.
      </p>
    </div>
  );
}
